import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, ListSubheader, Menu,
  MenuItem, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { useNotify } from "react-admin";
import { CLO_CHANNELS, CLO_NETWORKS, cloError, cloGet, cloPatch, cloPost, cloUpload, ENDPOINTS, formatDate } from "./api";
import { useRetailers } from "./useRetailers";

const READY_LABEL: Record<string, { label: string; color: "primary" | "secondary" | "default"; hint: string }> = {
  validated: { label: "Ready", color: "primary", hint: "Purchases at this MID earn rewards" },
  pending_validation: { label: "Waiting for check", color: "default", hint: "Not earning yet: purchases are held until the MID is marked Found" },
  pending_retailer_confirmation: { label: "Waiting for retailer", color: "default", hint: "Not earning yet: the retailer must confirm it" },
  unvalidated: { label: "Failed check", color: "secondary", hint: "Not earning; blocks campaign approval unless overridden" },
  rejected: { label: "Rejected", color: "default", hint: "Not used; purchases at this MID are ignored" },
};

const CHECK_LABEL: Record<string, string> = {
  found: "Found",
  not_found: "Not found",
  name_mismatch: "Name mismatch",
};

const SOURCE_LABEL: Record<string, string> = {
  retailer_file: "Retailer file",
  network_lookup: "Network search",
  queue_assigned: "Unknown-MID queue",
};

const CHANNEL_LABEL: Record<string, string> = {
  online: "Online",
  in_store: "In-store",
  delivery: "Delivery",
  click_and_collect: "Click & collect",
  marketplace: "Marketplace",
};

const EMPTY_MID = { brandId: "", network: "visa", mid: "", merchantName: "", location: "", channel: "in_store", source: "retailer_file" };
const EMPTY_VISA_IDS = { visaMerchantId: "", visaStoreId: "" };

const MidsTab: React.FC = () => {
  const notify = useNotify();
  const retailers = useRetailers();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [brandId, setBrandId] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: any } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<any>(EMPTY_MID);
  const [importOpen, setImportOpen] = useState(false);
  const [importBrand, setImportBrand] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any | null>(null);
  const [visaRow, setVisaRow] = useState<any | null>(null);
  const [visaIds, setVisaIds] = useState(EMPTY_VISA_IDS);

  const load = useCallback(async () => {
    try {
      const res = await cloGet(ENDPOINTS.CLO_MIDS, {
        page: page + 1, pageSize, brandId: brandId || undefined, status: status || undefined, search: search || undefined,
      });
      setRows(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [page, pageSize, brandId, status, search, notify]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const act = async (row: any, body: any, message: string): Promise<boolean> => {
    setMenu(null);
    try {
      await cloPatch(`${ENDPOINTS.CLO_MIDS}/${row.id}`, body);
      notify(message, "info");
      load();
      return true;
    } catch (e) {
      notify(cloError(e), "warning");
      return false;
    }
  };

  const openVisaIds = (row: any) => {
    setMenu(null);
    setVisaIds({ visaMerchantId: row.visa_merchant_id || "", visaStoreId: row.visa_store_id || "" });
    setVisaRow(row);
  };

  const saveVisaIds = async () => {
    const body = {
      action: "visa_ids",
      visaMerchantId: visaIds.visaMerchantId.trim() || null,
      visaStoreId: visaIds.visaStoreId.trim() || null,
    };
    if (await act(visaRow, body, "Visa IDs saved")) setVisaRow(null);
  };

  const addMid = async () => {
    try {
      await cloPost(ENDPOINTS.CLO_MIDS, { ...form, brandId: Number(form.brandId), merchantName: form.merchantName || null, location: form.location || null });
      notify("MID added", "info");
      setAddOpen(false);
      setForm(EMPTY_MID);
      load();
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const upload = async () => {
    if (!file || !importBrand) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("brandId", importBrand);
    try {
      const res = await cloUpload(ENDPOINTS.CLO_MIDS_IMPORT, formData);
      setImportResult(res.data);
      load();
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const retailerOptions = retailers.map((r) => (
    <MenuItem key={r.brand_id} value={String(r.brand_id)}>{r.display_name}</MenuItem>
  ));

  return (
    <Paper>
      <Box p={2} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
        <TextField select size="small" variant="outlined" label="Retailer" value={brandId} onChange={(e) => setBrandId(e.target.value)} style={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {retailerOptions}
        </TextField>
        <TextField select size="small" variant="outlined" label="Ready?" value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(READY_LABEL).map(([key, v]) => <MenuItem key={key} value={key}>{v.label}</MenuItem>)}
        </TextField>
        <TextField size="small" variant="outlined" label="Search MID / name" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Box flexGrow={1} />
        <Button variant="outlined" color="primary" onClick={() => { setImportResult(null); setFile(null); setImportOpen(true); }}>Import CSV</Button>
        <Button variant="contained" color="primary" onClick={() => setAddOpen(true)}>Add MID</Button>
      </Box>
      <Typography variant="caption" color="textSecondary" style={{ paddingLeft: 16 }}>
        Only "Ready" MIDs earn rewards; purchases at other MIDs are held until they're ready. The card-network lookup is not connected yet, so use ⋮ on a row to record the check result.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Retailer</TableCell>
            <TableCell>MID</TableCell>
            <TableCell>Shop</TableCell>
            <TableCell>Channel</TableCell>
            <TableCell>Added via</TableCell>
            <TableCell>Network check</TableCell>
            <TableCell>Ready?</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const ready = READY_LABEL[r.validation_status] || { label: r.validation_status, color: "default", hint: "" };
            return (
              <TableRow key={r.id} hover>
                <TableCell>{r.retailer_name}</TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2">{r.mid}</Typography>
                  <Typography variant="caption" color="textSecondary" display="block">{r.network === "visa" ? "Visa" : "Mastercard"}</Typography>
                  {(r.visa_merchant_id || r.visa_store_id) && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Visa merchant {r.visa_merchant_id || "—"} · store {r.visa_store_id || "—"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{r.merchant_name || "—"}</Typography>
                  <Typography variant="caption" color="textSecondary">{r.location || ""}</Typography>
                </TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>{CHANNEL_LABEL[r.channel] || r.channel}</TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>{SOURCE_LABEL[r.source] || r.source}</TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2">{r.validation_result ? CHECK_LABEL[r.validation_result] : "Not checked"}</Typography>
                  {r.validated_at && <Typography variant="caption" color="textSecondary">{formatDate(r.validated_at)}</Typography>}
                </TableCell>
                <TableCell>
                  <Tooltip title={ready.hint}>
                    <Chip size="small" label={ready.label} color={ready.color} />
                  </Tooltip>
                </TableCell>
                <TableCell align="right" padding="checkbox">
                  {r.validation_status !== "rejected" && (
                    <IconButton size="small" onClick={(e) => setMenu({ anchor: e.currentTarget, row: r })}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={8} align="center">No MIDs</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <Menu anchorEl={menu?.anchor || null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {menu?.row.validation_status === "pending_retailer_confirmation" ? (
          <MenuItem onClick={() => act(menu.row, { action: "confirm" }, "Confirmed by retailer")}>Retailer confirmed</MenuItem>
        ) : (
          [
            <ListSubheader key="h">Record network check</ListSubheader>,
            <MenuItem key="f" onClick={() => act(menu!.row, { action: "validate", result: "found" }, "Marked found")}>Found</MenuItem>,
            <MenuItem key="n" onClick={() => act(menu!.row, { action: "validate", result: "not_found" }, "Marked not found")}>Not found</MenuItem>,
            <MenuItem key="m" onClick={() => act(menu!.row, { action: "validate", result: "name_mismatch" }, "Marked name mismatch")}>Name mismatch</MenuItem>,
          ]
        )}
        <Divider />
        <MenuItem onClick={() => openVisaIds(menu!.row)}>Set Visa merchant / store ID</MenuItem>
        <Divider />
        <ListSubheader>Change channel</ListSubheader>
        {CLO_CHANNELS.filter((c) => c !== menu?.row.channel).map((c) => (
          <MenuItem key={c} onClick={() => act(menu!.row, { action: "channel", channel: c }, "Channel updated")}>{CHANNEL_LABEL[c]}</MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => act(menu!.row, { action: "reject" }, "MID rejected")} style={{ color: "#c62828" }}>Reject MID</MenuItem>
      </Menu>
      <TablePagination
        component="div" count={total} page={page} rowsPerPage={pageSize}
        onChangePage={(_, p) => setPage(p)}
        onChangeRowsPerPage={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
      />

      <Dialog disableBackdropClick open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a MID</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField select fullWidth margin="dense" label="Retailer" value={form.brandId} onChange={(e) => setForm({ ...form, brandId: e.target.value })}>{retailerOptions}</TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth margin="dense" label="Network" value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })}>
                {CLO_NETWORKS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth margin="dense" label="MID" value={form.mid} onChange={(e) => setForm({ ...form, mid: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth margin="dense" label="Channel" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                {CLO_CHANNELS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth margin="dense" label="Merchant name" value={form.merchantName} onChange={(e) => setForm({ ...form, merchantName: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth margin="dense" label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <TextField select fullWidth margin="dense" label="Where it came from" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                helperText="Found by searching the network → the retailer must confirm it">
                <MenuItem value="retailer_file">Retailer sent it</MenuItem>
                <MenuItem value="network_lookup">Found by network search</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={addMid}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog disableBackdropClick open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import MIDs from CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Columns: network, mid, merchant_name, location (optional), channel. Network: visa or mastercard. Channel: {CLO_CHANNELS.join(", ")}.
          </Typography>
          <Typography variant="body2" gutterBottom>
            Optional Visa columns: visa_merchant_id, visa_store_id, visa_result (found, not_found or name_mismatch). Found needs both Visa IDs.
            A MID that is already listed gets the Visa IDs and result from the file.
          </Typography>
          <TextField select fullWidth margin="dense" label="Retailer" value={importBrand} onChange={(e) => setImportBrand(e.target.value)}>{retailerOptions}</TextField>
          <Box mt={2}>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Box>
          {importResult && (
            <Box mt={2}>
              <Typography variant="body2">Imported {importResult.imported}, updated {importResult.updated ?? 0}, skipped {importResult.skipped}.</Typography>
              {(importResult.errors || []).slice(0, 50).map((err: any) => (
                <Typography key={err.line} variant="caption" display="block" color="error">Line {err.line}: {err.error}</Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Close</Button>
          <Button color="primary" variant="contained" disabled={!file || !importBrand} onClick={upload}>Upload</Button>
        </DialogActions>
      </Dialog>

      <Dialog disableBackdropClick open={visaRow !== null} onClose={() => setVisaRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Visa IDs for MID {visaRow?.mid}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>Digits only. Leave one empty to clear it; at least one is needed.</Typography>
          <TextField fullWidth margin="dense" label="Visa merchant ID" value={visaIds.visaMerchantId} onChange={(e) => setVisaIds({ ...visaIds, visaMerchantId: e.target.value })} />
          <TextField fullWidth margin="dense" label="Visa store ID" value={visaIds.visaStoreId} onChange={(e) => setVisaIds({ ...visaIds, visaStoreId: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisaRow(null)}>Cancel</Button>
          <Button color="primary" variant="contained" disabled={!visaIds.visaMerchantId.trim() && !visaIds.visaStoreId.trim()} onClick={saveVisaIds}>Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MidsTab;
